import { Injectable, OnDestroy } from '@angular/core';
import { Headers, Http, Request, RequestMethod, Response, RequestOptionsArgs } from '@angular/http';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs/Rx';
import { Store } from '@ngrx/store';

import { BaseHttpService } from './base-http.service';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import * as fromRoot from '../store/reducers';
import { AppUiActions, TestTakerActions } from '../store/actions';
import * as Enum from '../dtos/enums';
import { ConfigHelper } from './config.helper';

@Injectable()
export class TestTakerService extends BaseHttpService implements OnDestroy {
    testTakerInfo: PgRendererDto.TestTakerStationAssignment;
    testNavigation$: Observable<PgRendererDto.TestNavigation>;
    testNavigation: PgRendererDto.TestNavigation;
    private testTakerSubscription: Subscription;
    private testNavigationSubscription: Subscription;

    constructor(
        http: Http,
        httpClient: HttpClient,
        store: Store<fromRoot.State>,
        loggerService: LoggerService,
        authService: AuthService
    ) {
        super(http, httpClient, store, loggerService, authService);
        this.testTakerSubscription = store.select(fromRoot.getTestTakerInfo).subscribe(testTakerInfo => {
            this.testTakerInfo = testTakerInfo;
        });

        this.testNavigation$ = store.select(fromRoot.getTestNavigation);

        this.testNavigationSubscription = this.testNavigation$.subscribe(testNavigation => {
            this.testNavigation = testNavigation;
        });
    }

    ngOnDestroy() {
        this.testTakerSubscription.unsubscribe();
        this.testNavigationSubscription.unsubscribe();
    }

    getStationAssignmentWebDelivery(webDeliveryAuth: string): Observable<PgRendererDto.TestTakerStationAssignment> {
        const url = `${this.testServerApiUrl}/api/info/station`;
        return this.request({
            method: RequestMethod.Post,
            url: url,
            body: {
                authData: webDeliveryAuth
            }
        });
    }

    getStationAssignment(stationNumber: number): Observable<PgRendererDto.TestTakerStationAssignment> {
        const url = `${this.testServerApiUrl}/api/info/station/${stationNumber}`;
        return this.request({
            method: RequestMethod.Get,
            url: url
        });
    }

    getBlankTestTakerPhotoUrl(): string {
        return `${this.testContentServerApiUrl}/assets/img/paragon-icon.png`;
    }

    getTestTakerPhotoUrl(brandName: string, siteCode: string, regId: number): string {
        return `${this.testServerApiUrl}/api/info/test-taker/${brandName}/${siteCode}/${regId}`;
    }

    getWebDeliveryTestTakerPhotoUrl(clientApplicationSessionId: string, regId: number): string {
        return `${this.testServerApiUrl}/api/info/test-taker/${clientApplicationSessionId}/${regId}`;
    }

    saveTestTakerResponse(testTakerResponse: PgRendererDto.TestTakerResponse, speakingBlobData: PgRendererDto.SpeakingBlobData): Observable<any> {
        const url = `${this.testServerApiUrl}/api/test-taker/save-response/${this.testTakerInfo.TestTakerId}/${this.testTakerInfo.StationNumber}`;

        this.loggerService.reportDebugInfo("TestTakerService", {
            name: "saveTestTakerResponse",
            testTakerId: this.testTakerInfo.TestTakerId,
            step: "StartSaving"
        });

        for (let i = 0; i < testTakerResponse.InteractionResponses.length; i++) {
            this.loggerService.reportDebugInfo("TestTakerService", {
                name: "saveTestTakerResponse",
                testTakerId: this.testTakerInfo.TestTakerId,
                step: "InteractionResponse",
                interactionId: testTakerResponse.InteractionResponses[i].InteractionId
            });
        }

        const formData = new FormData();
        formData.append("TestTakerResponse", JSON.stringify(testTakerResponse));
        if (speakingBlobData) {
            this.loggerService.reportDebugInfo("TestTakerService", {
                name: "saveTestTakerResponse",
                testTakerId: this.testTakerInfo.TestTakerId,
                step: "SpeakingInteractionResponse",
                interactionId: speakingBlobData.interactionId.toString(),
                speakingBlobDataSize: speakingBlobData.blob != null ? speakingBlobData.blob.size : 0
            });
            formData.append("speakingBlobData", speakingBlobData.blob, speakingBlobData.interactionId.toString());
        }
        return this.request({
            method: RequestMethod.Post,
            url: url,
            body: formData
        }, true);
    }

    saveTestTakerKeyboardActivity(interactionId: number, keyboardActivityFile: Blob): Observable<any> {
        const url = `${this.testServerApiUrl}/api/test-taker/save-keyboard-activity/${this.testTakerInfo.TestTakerId}/${this.testTakerInfo.StationNumber}`;

        this.loggerService.reportDebugInfo("TestTakerService", {
            name: "saveTestTakerKeyboardActivtity",
            testTakerId: this.testTakerInfo.TestTakerId,
            step: "StartSaving"
        });

        const formData = new FormData();
        formData.append("InteractionId", interactionId.toString());
        formData.append("keyboardData", keyboardActivityFile, interactionId.toString());

        return this.request({
            method: RequestMethod.Post,
            url: url,
            body: formData
        }, true);
    }

    startTest(): Observable<PgRendererDto.TestInitializationInfo> {
        const url = `${this.testServerApiUrl}/api/start-test`;

        if (ConfigHelper.isTdpMode(true)) {
            const systemInformation: PgRendererDto.SystemInformation = {
                userHostAddress: "",
                userAgent: navigator.userAgent,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                browserWidth: window.innerWidth,
                browserHeight: window.innerHeight
            };
            return this.request({
                method: RequestMethod.Post,
                url: url,
                body: systemInformation
            });
        }

        return this.request({
            method: RequestMethod.Post,
            url: url
        });
    }

    authenticate(testTakerStationAssignment: PgRendererDto.TestTakerStationAssignment, secureBrowserVersionInfo: string): Observable<any> {
        const url = `${this.testServerApiUrl}/token`;
        const headers = new Headers();
        headers.append('Content-Type', 'application/x-www-form-urlencoded');
        headers.append("ClientApplicationSessionId", testTakerStationAssignment.ClientApplicationSessionId as any);
        headers.append("VersionInfoClaim", secureBrowserVersionInfo);
        headers.append("TestSessionSiteCode", testTakerStationAssignment.TestSessionSiteCode);
        headers.append("BrandName", testTakerStationAssignment.BrandName);

        return this.request({
            method: RequestMethod.Post,
            url: url,
            headers: headers,
            body: `username=${testTakerStationAssignment.TestTakerId}&password=${testTakerStationAssignment.TestSessionId}&grant_type=password`
        });
    }

    cacheTestTakerActivity(activityTypeId: Enum.TestTakerActivityTypeEnum, numericValue: number = null, textValue: string = null, testletId: number = null,
        interactionId: number = null, itemIndex: number = null) {
        this.store.dispatch(new TestTakerActions.CacheTestTakerActivities([{
            ActivityTypeId: activityTypeId,
            DateCreatedUtc: new Date(),
            InteractionId: interactionId,
            ItemIndex: itemIndex,
            NumericValue: numericValue,
            TestletId: testletId,
            TextValue: textValue,
            TestPartIndex: this.testNavigation ? this.testNavigation.testPartIndex : null,
            TestletIndex: this.testNavigation ? this.testNavigation.testletIndex : null
        }]));
    }

    saveTestTakerActivityWithoutCaching(activityTypeId: Enum.TestTakerActivityTypeEnum, numericValue: number = null, textValue: string = null,
        testletId: number = null, testletVersionId: number = null, interactionId: number = null, itemIndex: number = null) {
        if (ConfigHelper.isTdpMode()) {
            try {
                const activity = {
                    ActivityTypeId: activityTypeId,
                    DateCreatedUtc: new Date(),
                    InteractionId: interactionId,
                    ItemIndex: itemIndex,
                    NumericValue: numericValue,
                    TestletId: testletId,
                    TestletVersionId: testletVersionId,
                    TextValue: textValue
                };

                this.request({
                    method: RequestMethod.Post,
                    url: `${this.testServerApiUrl}/api/test-taker/save-activities`,
                    body: [ activity ]
                }).subscribe();
            } catch (error) {
                console.error(error);
            }
        }
    }

    ping(testTakerPing: PgRendererDto.TestTakerPing): Observable<any> {
        return this.request({
            method: RequestMethod.Post,
            url: `${this.testServerApiUrl}/api/ping`,
            body: testTakerPing
        });
    }

    getIppTestTakerInfo(assessmentTestId: number): Observable<PgRendererDto.TestTakerStationAssignment> {
        const url = `${this.testServerApiUrl}/api/test-taker/info/${assessmentTestId}`;

        return this.request({
            method: RequestMethod.Get,
            url: url
        }, true);
    }

    uploadTestTakerLog() {
        try {
            const url = `${this.testServerApiUrl}/api/log/upload-test-taker-log`;
            this.request({
                method: RequestMethod.Post,
                url: url
            }).subscribe();
        } catch (error) {
            console.error(error);
        }
    }

    isNonScoredTestStarted(): Observable<boolean> {
        const url = `${this.testServerApiUrl}/api/test-taker/is-non-scored-test-started`;
        return this.request({
            method: RequestMethod.Get,
            url: url
        });
    }
}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/services/test-taker.service.ts